import React from 'react';
import { Box, Paper, Button, Typography, CircularProgress, Alert, useTheme } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RefreshIcon from '@mui/icons-material/Refresh';

export interface QuestionFormToolbarProps {
    isDirty: boolean;
    dirtyCount: number;
    saving: boolean;
    onSave: () => void;
    onCancel: () => void;
    hasRemoteUpdate: boolean;
    onReload: () => void;
}

const QuestionFormToolbar: React.FC<QuestionFormToolbarProps> = ({
    isDirty,
    dirtyCount,
    saving,
    onSave,
    onCancel,
    hasRemoteUpdate,
    onReload,
}) => {
    const theme = useTheme();

    return (
        <>
            {/* Remote update banner */}
            {hasRemoteUpdate && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={onReload}
                        >
                            Reload
                        </Button>
                    }
                >
                    Questions were updated by another user or system process.
                    {isDirty && ' Your unsaved changes will be lost if you reload.'}
                </Alert>
            )}

            {/* Sticky save bar */}
            {isDirty && (
                <Paper
                    elevation={8}
                    sx={{
                        position: 'sticky',
                        bottom: 16,
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        backgroundColor: theme.palette.mode === 'dark'
                            ? theme.palette.background.paper
                            : theme.palette.background.paper,
                        border: `1px solid ${theme.palette.warning.main}`,
                    }}
                >
                    <Typography variant="body2" fontWeight={600}>
                        {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<UndoIcon />}
                            onClick={onCancel}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={onSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save All'}
                        </Button>
                    </Box>
                </Paper>
            )}
        </>
    );
};

export default QuestionFormToolbar;
